import { NgModule } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";

import { AppRoutingModule } from "./app-routing.module";
import { HttpClientModule, HTTP_INTERCEPTORS } from "@angular/common/http";
import { AppComponent } from "./app.component";
import { LoginComponent } from "./auth/login/login.component";
import { MainComponent } from "./main/main.component";
import { NavbarComponent } from "./main/navbar/navbar.component";
import { DashboardComponent } from "./main/dashboard/dashboard.component";
import { HeaderComponent } from "./shared/components/partials/header/header.component";
import { AlertComponent } from "./shared/components/partials/alert/alert.component";
import { AdvanceCostsComponent } from "./main/advance/costs/advance-costs.component";
import { ContextMenuComponent } from "./shared/components/partials/context-menu/context-menu.component";
import { CostComponent } from "./main/cost/cost.component";
import { InputComponent } from "./shared/components/form-control/input/input.component";
import { TextareaComponent } from "./shared/components/form-control/textarea/textarea.component";
import { SelectComponent } from "./shared/components/form-control/select/select.component";
import { CheckboxComponent } from "./shared/components/form-control/checkbox/checkbox.component";
import { NumberComponent } from "./shared/components/form-control/number/number.component";
import { FileComponent } from "./shared/components/form-control/file/file.component";
import { DateComponent } from "./shared/components/form-control/date/date.component";
import { TopbarComponent } from "./main/topbar/topbar.component";
import { CloseComponent } from "./shared/components/form-control/button/close/close.component";
import { PageNotFoundComponent } from "./error-pages/404/page-not-found.component";
import { PasswordComponent } from "./auth/login/password/password.component";
import { ProfileComponent } from "./main/profile/profile.component";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { LogoutComponent } from "./auth/logout/logout.component";
import { ContextMenuOptionComponent } from "./shared/components/partials/context-menu/context-menu-option/context-menu-option.component";
import { HeaderDetailComponent } from "./shared/components/partials/header/header-detail/header-detail.component";
import { ConfirmComponent } from "./shared/components/partials/confirm/confirm.component";
import { TopbarItemComponent } from "./main/topbar/topbar-item/topbar-item.component";
import { SettingsComponent } from "./main/settings/settings.component";
import { TwoFactorVerifyComponent } from "./auth/two-factor/two-factor-verify.component";
import { TwoFactorEnrollmentComponent } from "./auth/two-factor/two-factor-enrollment.component";
import { TwoFactorSetupPageComponent } from "./auth/two-factor/two-factor-setup-page.component";
import { TwoFactorSettingsComponent } from "./main/security/two-factor-settings.component";
import { OtpInputComponent } from "./shared/components/otp-input/otp-input.component";
import { DelegationsComponent } from "./main/delegations/delegations.component";
import { JwtInterceptor } from "./shared/interceptors/jwt.interceptor";
import { NewPasswordComponent } from "./auth/login/new-password/new-password.component";
import { SearchResultsComponent } from "./shared/components/partials/search-results/search-results.component";
import { EmployeesComponent } from "./main/employees/employees.component";
import { EmployeeComponent } from "./main/employee/employee.component";
import { SwitchComponent } from "./shared/components/form-control/switch/switch.component";
import { PagingComponent } from "./shared/components/partials/paging/paging.component";
import { HasPermissionDirective } from "./shared/directives/has-permission.directive";
import { ErrorInterceptor } from "./shared/interceptors/error.interceptor";
import { FulfillingSquareSpinnerModule } from "angular-epic-spinners";
import { LoaderInterceptor } from "./shared/interceptors/loader.interceptor";
import { LoaderComponent } from "./shared/components/partials/loader/loader.component";
import { AdvancesComponent } from "./main/advances/advances.component";
import { AdvanceComponent } from "./main/advance/advance.component";
import { AdvancesEmployeesComponent } from "./main/advances/employees/advances-employees.component";
import { ModalEmployeesComponent } from "./shared/partials/modals/employees/employees-modal.component";
import { ModalNoteComponent } from "./shared/partials/modals/note/note-modal.component";
import { AdvancesCostsComponent } from "./main/advances/costs/advances-costs.component";
import { CostsComponent } from "./main/costs/costs.component";
import { TranslatePipe } from "./shared/pipes/translate.pipe";
import { EnumPipe } from "./shared/pipes/enum.pipe";
import { AssetUrlPipe } from "./shared/pipes/asset-url.pipe";
import { registerLocaleData } from "@angular/common";
import localePl from "@angular/common/locales/pl";
import { CdkTableModule } from "@angular/cdk/table";
import { MatSortModule } from "@angular/material/sort";
import {
  MatCheckboxModule,
  MAT_CHECKBOX_CLICK_ACTION,
} from "@angular/material/checkbox";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { DelegationComponent } from "./main/delegation/delegation.component";
import { CostActionsTopComponent } from "./main/cost/cost-actions-top/cost-actions-top.component";
import { CostPrintComponent } from "./main/cost/cost-print/cost-print.component";
import { DelegationsEmployeesComponent } from "./main/delegations/employees/delegations-employees.component";
import { SelectOptionComponent } from "./shared/components/form-control/select/select-option/select-option.component";
import { AdvancesEmployeesArchivesComponent } from "./main/advances/employees/archives/advances-employees-archives.component";
import { AdvancesArchivesComponent } from "./main/advances/archives/advances-archives.component";
import { PageForbiddenComponent } from "./error-pages/403/page-forbidden/page-forbidden.component";
import { AdvanceEditComponent } from "./main/advance/edit/advance-edit.component";
import { ToastrModule } from "ngx-toastr";
import { EmployeeDelegationsComponent } from "./main/delegations/employee-delegations/employee-delegations.component";
import { EmployeeCostsComponent } from "./main/costs/employee-costs/employee-costs.component";
import { CostActionsBottomComponent } from "./main/cost/cost-actions-bottom/cost-actions-bottom.component";
import { SelectDropdownPanelComponent } from "./shared/components/form-control/select/select-dropdown-panel/select-dropdown-panel.component";
import { ModalAdvanceComponent } from "./shared/partials/modals/advance/advance-modal.component";
import { DelegationAddComponent } from "./main/delegation/add/delegation-add.component";
import { CostAddComponent } from "./main/cost/cost-add/cost-add.component";
import { FileSaverModule } from "ngx-filesaver";
import { NgSelectModule } from "@ng-select/ng-select";
import {
  MatDialogModule,
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatPaginator,
  MatPaginatorModule,
} from "@angular/material";
import { DevelopmentPanelComponent } from "./shared/components/partials/development-panel/development-panel.component";
import { ServiceWorkerModule } from "@angular/service-worker";
import { environment } from "../environments/environment";
import { DelegationsArchivesComponent } from "./main/delegations/archives/delegations-archives.component";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { SearchComponent } from "./shared/components/partials/search/search.component";
import { CostHistoryComponent } from "./main/cost/cost-history/cost-history.component";
import { SearchResultsForEmployeeComponent } from "./shared/components/partials/search-results-for-employee/search-results-for-employee.component";
import { AuctionIframeComponent } from "./test/auction-iframe/auction-iframe.component";
import { KsefGeneralListComponent } from "./main/ksef/pages/ksef-general-list/ksef-general-list.component";
import { KsefMyListComponent } from "./main/ksef/pages/ksef-my-list/ksef-my-list.component";
import { KsefInCirculationListComponent } from "./main/ksef/pages/ksef-in-circulation-list/ksef-in-circulation-list.component";
import { KsefInvoicesTableComponent } from "./main/ksef/components/ksef-invoices-table/ksef-invoices-table.component";
import { KsefCancelledListComponent } from "./main/ksef/pages/ksef-cancelled-list/ksef-cancelled-list.component";
import { FeatureFlagDirective } from "./shared/directives/feature-flag.directive";
import { KsefHrnestSettledListComponent } from "./main/ksef/pages/ksef-hrnest-settled-list/ksef-hrnest-settled-list.component";

registerLocaleData(localePl);

// TODO: Divide app module into submodules
@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    MainComponent,
    NavbarComponent,
    DashboardComponent,
    HeaderComponent,
    AlertComponent,
    AdvanceCostsComponent,
    CostComponent,
    ContextMenuComponent,
    InputComponent,
    TextareaComponent,
    SelectComponent,
    CheckboxComponent,
    NumberComponent,
    FileComponent,
    DateComponent,
    TopbarComponent,
    CloseComponent,
    PageNotFoundComponent,
    PasswordComponent,
    EmployeesComponent,
    ProfileComponent,
    LogoutComponent,
    ContextMenuOptionComponent,
    HeaderDetailComponent,
    ConfirmComponent,
    TopbarItemComponent,
    EmployeeComponent,
    SettingsComponent,
    TwoFactorVerifyComponent,
    TwoFactorEnrollmentComponent,
    TwoFactorSetupPageComponent,
    TwoFactorSettingsComponent,
    OtpInputComponent,
    DelegationsComponent,
    NewPasswordComponent,
    SearchResultsComponent,
    SearchResultsForEmployeeComponent,
    SwitchComponent,
    PagingComponent,
    HasPermissionDirective,
    FeatureFlagDirective,
    LoaderComponent,
    AdvancesComponent,
    AdvanceComponent,
    AdvancesEmployeesComponent,
    ModalEmployeesComponent,
    ModalNoteComponent,
    AdvancesCostsComponent,
    CostsComponent,
    TranslatePipe,
    EnumPipe,
    AssetUrlPipe,
    DelegationComponent,
    CostActionsTopComponent,
    CostPrintComponent,
    DelegationsEmployeesComponent,
    SelectOptionComponent,
    AdvancesEmployeesArchivesComponent,
    AdvancesArchivesComponent,
    PageForbiddenComponent,
    AdvanceEditComponent,
    EmployeeDelegationsComponent,
    EmployeeCostsComponent,
    CostActionsBottomComponent,
    SelectDropdownPanelComponent,
    ModalAdvanceComponent,
    DelegationAddComponent,
    CostAddComponent,
    DevelopmentPanelComponent,
    DelegationsArchivesComponent,
    SearchComponent,
    CostHistoryComponent,
    AuctionIframeComponent,
      KsefGeneralListComponent,
      KsefMyListComponent,
      KsefInCirculationListComponent,
      KsefInvoicesTableComponent,
      KsefCancelledListComponent,
      KsefHrnestSettledListComponent,
  ],
  imports: [
    BrowserModule,
    HttpClientModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    NgSelectModule,
    FormsModule,
    ReactiveFormsModule,
    CdkTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatCheckboxModule,
    MatDialogModule,
    FulfillingSquareSpinnerModule,
    FileSaverModule,
    ToastrModule.forRoot({
      positionClass: "toast-top-center",
      timeOut: 3000,
    }),
    ServiceWorkerModule.register("ngsw-worker.js", {
      enabled: environment.production,
    }),
    MatAutocompleteModule,
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true },
    { provide: MAT_CHECKBOX_CLICK_ACTION, useValue: "check" },
    { provide: MatDialogRef, useValue: {} },
    { provide: MAT_DIALOG_DATA, useValue: [] },
  ],
  entryComponents: [ConfirmComponent],
  bootstrap: [AppComponent],
})
export class AppModule {}
